import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Reveal } from "./common";

const fmt = (n) => "£" + Math.round(n).toLocaleString();

export default function FinanceCalculator() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(150000);
  const [deposit, setDeposit] = useState(15000);
  const [term, setTerm] = useState(48);
  const [rate] = useState(8.9);

  const { monthly, principal, totalRepayable } = useMemo(() => {
    const principal = Math.max(amount - deposit, 0);
    const r = rate / 100 / 12;
    const n = term;
    const monthly = r === 0 ? principal / n : (principal * r) / (1 - Math.pow(1 + r, -n));
    return { monthly, principal, totalRepayable: monthly * n };
  }, [amount, deposit, term, rate]);

  return (
    <section data-testid="finance-calculator" className="bg-[#1A1A1A] py-24 md:py-32 border-y border-[#808080]/20">
      <div className="container-x mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-lime mb-4">Finance Calculator</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-6">Estimate your monthly repayment</h2>
            <p className="text-grey text-lg leading-relaxed mb-8">Adjust the figures to get an instant indication. This is a guide only, your adviser will provide tailored rates from across our lender panel.</p>

            <div className="space-y-8">
              <Slider label="Funding amount" value={amount} min={10000} max={2000000} step={5000} onChange={setAmount} display={fmt(amount)} testid="calc-amount" />
              <Slider label="Deposit" value={deposit} min={0} max={Math.min(amount * 0.5, amount)} step={1000} onChange={setDeposit} display={fmt(deposit)} testid="calc-deposit" />
              <Slider label="Term" value={term} min={12} max={120} step={6} onChange={setTerm} display={`${term} months`} testid="calc-term" />
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="bg-black border border-[#808080]/30 p-8 md:p-10">
              <p className="text-xs uppercase tracking-[0.3em] text-grey mb-3">Estimated monthly payment</p>
              <div data-testid="calc-monthly-result" className="text-5xl md:text-6xl font-black text-lime tracking-tight mb-8">{fmt(monthly)}</div>
              <div className="grid grid-cols-2 gap-px bg-[#808080]/20 border border-[#808080]/20">
                {[["Amount financed", fmt(principal)], ["Indicative rate", `${rate}% p.a.`], ["Term", `${term} mo`], ["Total repayable", fmt(totalRepayable)]].map(([l, v]) => (
                  <div key={l} className="bg-black p-5">
                    <p className="text-xs text-grey uppercase tracking-wide">{l}</p>
                    <p className="text-xl font-bold mt-1">{v}</p>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate("/contact?src=calculator")} data-testid="calc-apply-btn" className="w-full mt-8 bg-lime text-[#1A1A1A] font-bold py-4 hover:glow-lime transition-shadow">Get a tailored quote</button>
              <p className="text-xs text-grey mt-4 text-center">Representative example. Rates subject to status and lender criteria.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Slider({ label, value, min, max, step, onChange, display, testid }) {
  return (
    <div>
      <div className="flex justify-between items-baseline mb-3">
        <label className="text-sm uppercase tracking-wide text-white/80">{label}</label>
        <span className="text-xl font-bold text-lime">{display}</span>
      </div>
      <input data-testid={testid} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full" />
    </div>
  );
}
